import { useCallback, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  adminSetPassword,
  authCreateAdmin,
  fetchAdminUsers,
  getAdminUser,
  reactivateAdminUser,
} from "../api/adminClient";
import AdminConsoleTabs from "./AdminConsoleTabs";

const TABS = [
  { id: "reset", label: "Reset password" },
  { id: "create", label: "Create admin" },
  { id: "reactivate", label: "Reactivate" },
];

export default function AdminOpsPage() {
  const adminUser = getAdminUser();

  const [tab, setTab] = useState("reset");
  const [loading, setLoading] = useState(false);

  // Reset password state
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  // Create admin state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminConfirm, setAdminConfirm] = useState("");

  // Reactivate state
  const [disabledUsers, setDisabledUsers] = useState([]);
  const [disabledLoading, setDisabledLoading] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);

  const loadDisabledUsers = useCallback(async () => {
    setDisabledLoading(true);
    try {
      const res = await fetchAdminUsers({ status: "disabled" });
      setDisabledUsers(res.data?.users || []);
    } catch (err) {
      toast.error(err.message || "Could not load deactivated admins.");
    } finally {
      setDisabledLoading(false);
    }
  }, []);

  const loadAdminUsers = useCallback(async () => {
    try {
      const res = await fetchAdminUsers({ status: "active" });
      setAdminUsers(res.data?.users || []);
    } catch {
      setAdminUsers([]);
    }
  }, []);

  useEffect(() => {
    if (tab === "reactivate") loadDisabledUsers();
    if (tab === "reset") loadAdminUsers();
  }, [tab, loadDisabledUsers, loadAdminUsers]);

  const handleReactivate = async (user) => {
    if (confirmId !== user.id) {
      setConfirmId(user.id);
      return;
    }
    setReactivatingId(user.id);
    try {
      await reactivateAdminUser(user.id);
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
      toast.success(`${name} reactivated.`);
      setDisabledUsers((prev) => prev.filter((u) => u.id !== user.id));
      setConfirmId(null);
    } catch (err) {
      toast.error(err.message || "Could not reactivate this admin.");
    } finally {
      setReactivatingId(null);
    }
  };

  const formatDisabledAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const resetResetFlow = () => {
    setResetEmail("");
    setNewPassword("");
    setNewPasswordConfirm("");
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await adminSetPassword(resetEmail.trim(), newPassword);
      toast.success(res.message || "Password updated. Share it with the admin user.");
      resetResetFlow();
    } catch (err) {
      toast.error(err.message || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (adminPassword !== adminConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await authCreateAdmin({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: adminEmail.trim(),
        phone: phone.trim(),
        city: city.trim(),
        password: adminPassword,
      });
      toast.success(res.message || "Admin created.");
      setFirstName("");
      setLastName("");
      setAdminEmail("");
      setPhone("");
      setCity("");
      setAdminPassword("");
      setAdminConfirm("");
    } catch (err) {
      toast.error(err.message || "Could not create admin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full w-full bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-center" />
      <div className="w-full px-7 py-8">
        <header className="mb-7">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0a7ea4]">
            Admin
          </p>
          <h1 className="text-[28px] font-bold leading-tight text-slate-900 dark:text-slate-100">
            Admin access
          </h1>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            Signed in as {adminUser?.email || adminUser?.name || "admin"}
          </p>
        </header>

        <AdminConsoleTabs active="ops" />

        <div className="rounded-[20px] border border-slate-200/80 dark:border-slate-700/70 bg-white dark:bg-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(0,79,115,0.18)] overflow-hidden max-w-2xl">
          {/* In-page tab strip */}
          <div className="flex items-center gap-1 px-5 pt-5 pb-0 flex-wrap" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`px-4 py-2 text-sm font-semibold rounded-xl border-b-2 -mb-px transition-colors ${
                  tab === t.id
                    ? "border-[#004f73] text-[#004f73] dark:border-[#22d3ee] dark:text-[#22d3ee]"
                    : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
                onClick={() => {
                  setTab(t.id);
                  setConfirmId(null);
                  if (t.id === "reset") resetResetFlow();
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="border-t border-slate-100 dark:border-slate-800" />

          <div className="p-6">
            {/* ── Reset password ── */}
            {tab === "reset" && (
              <form onSubmit={handlePasswordReset} className="flex flex-col gap-3 max-w-sm">
                <div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-0.5">
                    Reset password
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Set a new password for this admin, then share it with them so they can sign in.
                  </p>
                </div>
                <div>
                  <label
                    className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5"
                    htmlFor="reset-email"
                  >
                    Admin email
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    list="reset-admin-emails"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm leading-6 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="admin@company.com"
                    required
                  />
                  <datalist id="reset-admin-emails">
                    {adminUsers.map((u) => (
                      <option key={u.id} value={u.email}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ")}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="new-password">
                    New password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="new-password-confirm">
                    Confirm password
                  </label>
                  <input
                    id="new-password-confirm"
                    type="password"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg disabled:opacity-50 transition-colors"
                  disabled={loading}
                >
                  {loading ? "Updating…" : "Set password"}
                </button>
              </form>
            )}

            {/* ── Create admin ── */}
            {tab === "create" && (
              <form onSubmit={handleCreate} className="flex flex-col gap-3 max-w-sm">
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Create admin</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-first">
                      First name
                    </label>
                    <input
                      id="create-first"
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-last">
                      Last name
                    </label>
                    <input
                      id="create-last"
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-email">
                    Email
                  </label>
                  <input
                    id="create-email"
                    type="email"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-phone">
                      Phone (optional)
                    </label>
                    <input
                      id="create-phone"
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-city">
                      City (optional)
                    </label>
                    <input
                      id="create-city"
                      className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-password">
                    Password
                  </label>
                  <input
                    id="create-password"
                    type="password"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="create-confirm">
                    Confirm password
                  </label>
                  <input
                    id="create-confirm"
                    type="password"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                    value={adminConfirm}
                    onChange={(e) => setAdminConfirm(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg disabled:opacity-50 transition-colors"
                  disabled={loading}
                >
                  {loading ? "Creating…" : "Create admin account"}
                </button>
              </form>
            )}

            {/* ── Reactivate ── */}
            {tab === "reactivate" && (
              <div className="max-w-sm">
                <div className="mb-4">
                  <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-0.5">
                    Reactivate
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Restore a deactivated admin so they can sign in again.
                  </p>
                </div>
                {disabledLoading ? (
                  <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
                ) : !disabledUsers.length ? (
                  <p className="py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                    No deactivated admins
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                    {disabledUsers.map((user) => {
                      const name =
                        [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
                      const when = formatDisabledAt(user.disabled_at);
                      const confirming = confirmId === user.id;
                      return (
                        <li
                          key={user.id}
                          className="flex items-center gap-3 px-3.5 py-3 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/60"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                              {name}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                            {when ? (
                              <p className="text-[11px] text-slate-400 dark:text-slate-500">
                                Deactivated {when}
                              </p>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-50 ${
                              confirming
                                ? "bg-teal-600 text-white hover:bg-teal-700"
                                : "bg-[#004f73] text-white hover:bg-[#003a57]"
                            }`}
                            disabled={reactivatingId === user.id}
                            onClick={() => handleReactivate(user)}
                            onBlur={() => {
                              if (confirmId === user.id && reactivatingId !== user.id)
                                setConfirmId(null);
                            }}
                          >
                            {reactivatingId === user.id
                              ? "Restoring…"
                              : confirming
                                ? "Confirm"
                                : "Reactivate"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
